import { formatNumberWithTooltip } from 'components/tooltip-number';
import { InterestSourcePill } from 'components/interest-source-pill';
import { TotalUnitInterestCard } from 'components/total-unit-interest-card';
import type { UnitInterestTableItem } from 'components/total-unit-interest-card/types';
import { TractNriMatrixCard } from 'components/tract-nri-matrix-card';
import { OverAllocationWarning } from 'components/nri-table/over-allocation-warning';
import type { CalculationType, TractNRI } from 'components/nri-table/types';
import { useOptionalEval3Page } from './context';
import styles from './total-slot-interest-cell.module.scss';

interface TotalSlotInterestCellProps {
  /** The row, supplying the subtract rollup + tract/slot identity for the popups. */
  row: TractNRI;
  /** The Total NRI or WI value. */
  value: number | null;
  /** Which interest the cell shows. */
  calculationType: CalculationType;
}

/**
 * Slot-level Total NRI / WI cell: the value with a precision tooltip, the
 * per-subtract verification rollup donut (`SlotRollupVerificationStatus`, driven
 * by the row's subtracts), plus the two reused formula popups — the calculation
 * rollup (`TotalUnitInterestCard`, prop-driven from the row's subtracts) and the
 * NRI/WI matrix (`TractNriMatrixCard`, keyed by tract, and by the page's package so a
 * Tract the package holds in part sums only its Subtracts). Shared by the Slot view,
 * the Slot Allocation view, and the Asset expanded row's slot-allocation table.
 * The donut/rollup are hidden when the row has no subtracts; the matrix when the
 * row has no tract/slot identity. The rollup's
 * subtract names are non-navigable here by design — legacy threads an
 * `onTractClick`; eval3-new has no tract-nav handler in scope.
 *
 * @param {TotalSlotInterestCellProps} props - Row, value, and interest type.
 * @return {JSX.Element} The cell with value + the two formula popups.
 */
export function TotalSlotInterestCell({
  row,
  value,
  calculationType,
}: TotalSlotInterestCellProps) {
  const subtracts = row.subtracts ?? [];
  const items: UnitInterestTableItem[] = subtracts.map((subtract) => ({
    subtract: subtract.crmSubtractId ?? '',
    subtractSalesforceId: subtract.crmSubtractSalesforceId,
    tractSalesforceId: subtract.crmTractSalesforceId,
    uat: subtract.uat ?? '',
    value: subtract.allocationValue,
    valueUnit: subtract.allocationValueUnit,
    factor: subtract.allocationFactor,
    uatInterest:
      (calculationType === 'NRI' ? subtract.uatNri : subtract.uatWi) ?? 0,
    nriCalculationId: subtract.nriCalculationId,
  }));
  const tractId = row.crmTractSalesforceId;
  const { slotId } = row;
  const isLtd = row.wellStatus === 'LTD';

  const isCertified = row.wiSource === 'certified';
  const packageId = useOptionalEval3Page()?.packageId;

  return (
    <span className={styles.cell}>
      {formatNumberWithTooltip(value, 8)}
      <OverAllocationWarning value={value} measure={calculationType} />
      <InterestSourcePill
        wiSource={row.wiSource}
        nriCertifiedWorkspaceId={row.nriCertifiedWorkspaceId}
        interestEntityId={row.interestEntityId}
      />
      {subtracts.length > 0 && !isCertified && (
        <TotalUnitInterestCard
          slotName={row.wellName ?? ''}
          items={items}
          calculatedValue={value ?? 0}
          calculationType={calculationType}
        />
      )}
      {tractId && slotId && !isCertified && (
        <TractNriMatrixCard
          tractId={tractId}
          slotId={isLtd ? slotId : undefined}
          wellUwi={isLtd ? undefined : slotId}
          packageId={packageId}
          calculationType={calculationType}
        />
      )}
    </span>
  );
}
